import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Truck, Search, Briefcase, Receipt, Heart, ShieldCheck, CheckCircle2,
  Route as RouteIcon, Award, ArrowRight, History, FileText, X,
} from "lucide-react";
import DriverTier from "../Includes/DriverTier";

// ── Design tokens ─────────────────────────────────────────────────────────
// Ported 1:1 from the "Premium Logistics Framework" design system (ink navy
// + brass/gold, Enterprise/Financial aesthetic — private-aviation-grade,
// not a to-do app). Same tokens Invoices.jsx already uses, so the two pages
// read as one product instead of two different apps.
const INK_NAVY = "#0B1220";
const SURFACE_NAVY = "#141F38";
const BRASS = "#B8862E";
const GOLD_LIGHT = "#D9A94A";
const STATUS_SUCCESS = "#10B981";
const BORDER_BRASS = "rgba(184,134,46,0.3)";
const FILL_BRASS_SUBTLE = "rgba(184,134,46,0.04)";
const OUTLINE_VARIANT = "#e5e7eb";
const ON_SURFACE_VARIANT = "#6b7280";

// ── Drill-down modal shared by the Professional Standing metrics — real
// data (completed jobs from GET /api/loads/assignments/mine/), not
// fabricated per-job numbers where the backend doesn't actually track them.
function StatDrillDownModal({ kind, jobs, driverProfile, onClose, onNavigateInvoices }) {
  const titles = {
    earnings: "Earnings by job",
    completed: "Completed jobs",
    miles: "Miles by job",
    rating: "Rating",
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0" style={{ background: INK_NAVY }}>
          <h3 className="font-black text-white text-sm tracking-tight">{titles[kind]}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" aria-label="Close"><X className="w-4.5 h-4.5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {kind === "rating" ? (
            <div className="text-center py-4">
              <p className="text-3xl font-black" style={{ color: BRASS }}>{driverProfile.rating ? Number(driverProfile.rating).toFixed(1) : "—"} ★</p>
              <p className="text-xs text-gray-400 mt-2 max-w-[220px] mx-auto">
                This is your overall account rating. Per-job reviews aren't tracked yet on the backend, so a breakdown by job isn't possible here yet.
              </p>
            </div>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No completed jobs yet.</p>
          ) : (
            <div className="space-y-2">
              {jobs.map(job => (
                <div key={job.id} className="flex items-center justify-between p-3 border rounded-xl" style={{ borderColor: OUTLINE_VARIANT }}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{job.load?.from} → {job.load?.to}</p>
                    <p className="text-[11px] text-gray-400">{job.load?.id || job.id.slice(0, 8)}</p>
                  </div>
                  {kind === "earnings" && (
                    <span className="text-sm font-bold flex-shrink-0" style={{ color: STATUS_SUCCESS }}>
                      {job.invoice_total != null ? `$${Number(job.invoice_total).toLocaleString()}` : "Not invoiced"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {kind === "earnings" && jobs.length > 0 && (
            <button
              onClick={onNavigateInvoices}
              className="w-full mt-4 text-xs font-bold text-center py-2 rounded-lg hover:opacity-80 transition-opacity"
              style={{ color: BRASS }}
            >
              Manage invoices →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Redesign notes ───────────────────────────────────────────────────────
// Rebuilt to match the "Enterprise Logistics Command Center" design
// (ink-navy + brass, Inter type, institutional precision) instead of the
// generic blue/emerald/purple mix. Same data contract as before — nothing
// here is fabricated:
//   1. Greeting card (ink navy) surfaces real lifetime totals, not a fake
//      "this week" number the backend doesn't track.
//   2. Active Mission widget — real, fetched, clickable — or an idle state
//      that's honest about there being no fabricated "progress %" (the
//      backend only tracks status, not percent-complete).
//   3. Quick actions grid, four real routes.
//   4. Professional Standing — replaces the old four-card stat wall with
//      three data-dense metric blocks (rating / completed / miles), each
//      clickable into the same real per-job drill-down as before.
//   5. Driver Tier kept below the fold, unchanged (separate component).
//   6. Recent Activity — a real table pulled from /api/invoices/, in the
//      REFERENCE / CLIENT · ROUTE / AMOUNT / STATUS shape.
//   7. Per-job notes box, restyled to match, still local-only (noted below).
export default function Overview() {
  const navigate = useNavigate();
  const { user, completion } = useOutletContext() || {};

  const driverProfile = user?.driverProfile || user?.driver_profile || {};

  const [activeLoad, setActiveLoad] = useState(null);
  const [loadingActive, setLoadingActive] = useState(true);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [openStat, setOpenStat] = useState(null); // "earnings" | "completed" | "miles" | "rating" | null

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");
    if (!token) { setLoadingActive(false); return; }
    const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
    Promise.all([
      fetch(`${apiUrl}/api/loads/assignments/mine/`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch(`${apiUrl}/api/invoices/`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ])
      .then(([list, invoices]) => {
        if (cancelled) return;
        const active = (list || []).find(a => ["started", "in_transit", "paused"].includes(a.status));
        setActiveLoad(active || null);
        const invoiceByAssignment = Object.fromEntries((invoices || []).map(i => [i.assignment, i.total]));
        setCompletedJobs(
          (list || [])
            .filter(a => a.status === "completed")
            .map(a => ({ ...a, invoice_total: invoiceByAssignment[a.id] ?? null }))
        );
        setRecentInvoices((invoices || []).slice(0, 5));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingActive(false); });
    return () => { cancelled = true; };
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  })();
  const firstName = user?.first_name || user?.full_name?.split(" ")[0] || "Driver";

  const totalEarnings = driverProfile.total_earnings ? `$${Number(driverProfile.total_earnings).toLocaleString()}` : "$0";
  const totalMiles = driverProfile.total_miles ? Number(driverProfile.total_miles).toLocaleString() : "0";
  const rating = driverProfile.rating ? Number(driverProfile.rating).toFixed(1) : null;
  const completedCount = driverProfile.completed_loads_count || 0;

  const quickActions = [
    { icon: Search, label: "Load Board", sub: "Browse & apply", to: "/load-board" },
    { icon: Briefcase, label: "My Jobs", sub: "Offers & history", to: "/my-jobs" },
    { icon: Receipt, label: "Invoices", sub: "Manage billing", to: "/invoices" },
    { icon: Heart, label: "Saved Loads", sub: "Your shortlist", to: "/saved-loads" },
  ];

  return (
    <div className="space-y-6" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      {/* Greeting + Active Mission */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Premium greeting card */}
        <div
          className="lg:col-span-2 rounded-2xl p-6 shadow-sm relative overflow-hidden border flex flex-col justify-between min-h-[172px]"
          style={{ background: SURFACE_NAVY, borderColor: BORDER_BRASS }}
        >
          <div className="absolute right-0 top-0 w-64 h-full blur-3xl rotate-12 translate-x-1/4 pointer-events-none" style={{ background: "rgba(184,134,46,0.06)" }} />
          <div className="relative z-10">
            <h1 className="text-white text-xl font-bold mb-1.5">{greeting}, {firstName} 👋</h1>
            <p className="text-sm max-w-md leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
              {activeLoad
                ? "Your command center is updated — you have 1 active mission currently en route."
                : "Your command center is ready. No active mission right now — find your next load below."}
            </p>
          </div>
          <div className="relative z-10 flex gap-8 mt-6 pt-4 border-t" style={{ borderColor: "rgba(184,134,46,0.2)" }}>
            <button onClick={() => setOpenStat("earnings")} className="text-left hover:opacity-80 transition-opacity">
              <span className="text-[10px] font-black uppercase tracking-[0.15em] block mb-1" style={{ color: GOLD_LIGHT }}>Total Earnings</span>
              <span className="text-2xl font-black text-white tracking-tight">{totalEarnings}</span>
            </button>
            <button onClick={() => setOpenStat("miles")} className="text-left hover:opacity-80 transition-opacity">
              <span className="text-[10px] font-black uppercase tracking-[0.15em] block mb-1" style={{ color: "rgba(255,255,255,0.5)" }}>Total Miles</span>
              <span className="text-lg font-bold text-white">{totalMiles}</span>
            </button>
          </div>
        </div>

        {/* Active Mission widget */}
        {loadingActive ? (
          <div className="bg-white rounded-2xl border p-5 shadow-sm animate-pulse min-h-[172px]" style={{ borderColor: OUTLINE_VARIANT }} />
        ) : activeLoad ? (
          <button
            onClick={() => navigate(`/my-jobs/console/${activeLoad.id}`)}
            className="text-left bg-white rounded-2xl p-5 shadow-sm border hover:shadow-md transition-all flex flex-col justify-between group min-h-[172px]"
            style={{ borderColor: OUTLINE_VARIANT }}
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-black uppercase tracking-[0.15em] flex items-center gap-1.5" style={{ color: STATUS_SUCCESS }}>
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: STATUS_SUCCESS }} />
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: STATUS_SUCCESS }} />
                  </span>
                  Active Mission
                </span>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-current transition-colors" style={{ color: "inherit" }} />
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg flex-shrink-0" style={{ background: "rgba(16,185,129,0.1)", color: STATUS_SUCCESS }}>
                  <Truck className="w-4.5 h-4.5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm leading-snug truncate">{activeLoad.load?.from} → {activeLoad.load?.to}</h3>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{activeLoad.load?.commodity || "Oversize Load"} · Escort Required</p>
                </div>
              </div>
            </div>
            <div className="rounded-lg p-3 border mt-4" style={{ background: "#f9fafb", borderColor: OUTLINE_VARIANT }}>
              <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">Status</span>
              <p className="text-xs font-bold text-gray-800 mt-0.5 capitalize">{String(activeLoad.status).replace("_", " ")} — tap to continue</p>
            </div>
          </button>
        ) : (
          <button
            onClick={() => navigate("/load-board")}
            className="text-left bg-white rounded-2xl p-5 shadow-sm border hover:border-current transition-all flex flex-col justify-between group min-h-[172px]"
            style={{ borderColor: OUTLINE_VARIANT, color: BRASS }}
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">No Active Mission</span>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-current transition-colors" />
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg flex-shrink-0" style={{ background: FILL_BRASS_SUBTLE, color: BRASS }}>
                  <Search className="w-4.5 h-4.5" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Find your next load</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Browse the live board</p>
                </div>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {quickActions.map(a => (
          <button
            key={a.label}
            onClick={() => navigate(a.to)}
            className="bg-white rounded-2xl p-4 shadow-sm border hover:shadow-md transition-all group flex items-start gap-3 text-left"
            style={{ borderColor: OUTLINE_VARIANT }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = BRASS; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = OUTLINE_VARIANT; }}
          >
            <div
              className="p-2 rounded-lg flex-shrink-0 transition-colors"
              style={{ background: "#f3f4f6", color: INK_NAVY }}
            >
              <a.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 leading-tight">{a.label}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 truncate">{a.sub}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Quick job notes — scratchpad for the active job. Still local-only:
          stored per-device in localStorage, since LoadAssignment has no
          driver-notes field of its own on the backend yet (only the
          pre-award LoadApplication.driver_notes, a different stage). */}
      {activeLoad && <JobNotesBox jobId={activeLoad.id} />}

      {/* Professional Standing — replaces the old four-tile stat wall with
          real, data-dense metrics. No fabricated "safety rating" or
          "reliability %" — only fields the backend actually tracks. */}
      <section>
        <div className="flex items-center gap-2.5 mb-3">
          <Award className="w-4 h-4" style={{ color: BRASS }} />
          <h3 className="text-sm font-bold text-gray-900">Professional Standing</h3>
          <div className="h-px flex-1 ml-2" style={{ background: `linear-gradient(to right, ${BORDER_BRASS}, transparent)` }} />
        </div>
        <div className="rounded-2xl border p-5 sm:p-6 shadow-sm" style={{ background: FILL_BRASS_SUBTLE, borderColor: BORDER_BRASS }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <button onClick={() => setOpenStat("rating")} className="text-left">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: ON_SURFACE_VARIANT }}>Rating</span>
                <ShieldCheck className="w-3.5 h-3.5" style={{ color: BRASS }} />
              </div>
              <div className="text-2xl font-black tracking-tight" style={{ color: INK_NAVY }}>{rating ? `${rating} ★` : "New"}</div>
              <p className="text-[11px] mt-1" style={{ color: ON_SURFACE_VARIANT }}>Overall account rating</p>
            </button>
            <button onClick={() => setOpenStat("completed")} className="text-left sm:border-l sm:pl-6" style={{ borderColor: BORDER_BRASS }}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: ON_SURFACE_VARIANT }}>Completed Jobs</span>
                <CheckCircle2 className="w-3.5 h-3.5" style={{ color: STATUS_SUCCESS }} />
              </div>
              <div className="text-2xl font-black tracking-tight" style={{ color: INK_NAVY }}>{completedCount}</div>
              <p className="text-[11px] mt-1" style={{ color: ON_SURFACE_VARIANT }}>Loads delivered to date</p>
            </button>
            <button onClick={() => setOpenStat("miles")} className="text-left sm:border-l sm:pl-6" style={{ borderColor: BORDER_BRASS }}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: ON_SURFACE_VARIANT }}>Total Logged Miles</span>
                <RouteIcon className="w-3.5 h-3.5" style={{ color: ON_SURFACE_VARIANT }} />
              </div>
              <div className="text-2xl font-black tracking-tight" style={{ color: INK_NAVY }}>{totalMiles}</div>
              <p className="text-[11px] mt-1" style={{ color: ON_SURFACE_VARIANT }}>Across all completed loads</p>
            </button>
          </div>
        </div>
      </section>

      {/* Driver Tier / Progressive Achievement — kept as its own component */}
      {/* <DriverTier driverProfile={driverProfile} /> */}

      {/* Recent Activity — real invoices, most recent first */}
      <section>
        <div className="flex justify-between items-end mb-3 border-b pb-2" style={{ borderColor: OUTLINE_VARIANT }}>
          <div className="flex items-center gap-2.5">
            <History className="w-4 h-4" style={{ color: INK_NAVY }} />
            <h3 className="text-sm font-bold text-gray-900">Recent Activity</h3>
          </div>
          <button onClick={() => navigate("/invoices")} className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1 hover:opacity-80" style={{ color: BRASS }}>
            View All <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: OUTLINE_VARIANT }}>
          {recentInvoices.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-7 h-7 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No invoices yet — complete a job to create one.</p>
            </div>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.1em]" style={{ background: "#f9fafb", color: ON_SURFACE_VARIANT, borderBottom: `1px solid ${OUTLINE_VARIANT}` }}>
                <div className="col-span-3">Reference</div>
                <div className="col-span-5">Client / Route</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-2 text-right">Status</div>
              </div>
              {recentInvoices.map((inv, i) => (
                <button
                  key={inv.id}
                  onClick={() => navigate("/invoices")}
                  className="w-full text-left grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-4 items-center hover:bg-gray-50 transition-colors"
                  style={{ borderBottom: i === recentInvoices.length - 1 ? "none" : `1px solid ${OUTLINE_VARIANT}` }}
                >
                  <div className="col-span-2 sm:col-span-3 flex items-center gap-3 min-w-0">
                    <div className="p-1.5 rounded flex-shrink-0" style={{ background: "#f3f4f6", color: ON_SURFACE_VARIANT }}>
                      <FileText className="w-3.5 h-3.5" />
                    </div>
                    <p className="text-sm font-bold truncate" style={{ color: INK_NAVY }}>#{inv.invoice_number}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-5 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{inv.bill_to_company || "No company on file"}</p>
                    <p className="text-[11px] text-gray-400 truncate">{inv.load_route}</p>
                  </div>
                  <div className="text-sm font-bold text-gray-800 sm:text-right sm:col-span-2">${Number(inv.total).toLocaleString()}</div>
                  <div className="sm:text-right sm:col-span-2">
                    <span
                      className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full inline-block"
                      style={
                        inv.status === "sent" ? { background: "rgba(16,185,129,0.1)", color: STATUS_SUCCESS } :
                        inv.status === "paid" ? { background: "rgba(37,99,235,0.1)", color: "#2563EB" } :
                        { background: FILL_BRASS_SUBTLE, border: `1px solid ${BORDER_BRASS}`, color: "#8A651F" }
                      }
                    >{inv.status}</span>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </section>

      {/* Profile Completion */}
      {(completion || 0) < 100 && (
        <div className="bg-white rounded-2xl p-5 border shadow-sm" style={{ borderColor: OUTLINE_VARIANT }}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-bold text-gray-700">Profile Completion</span>
            <span className="text-xs font-black" style={{ color: BRASS }}>{completion || 0}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${completion || 0}%`, background: BRASS }} />
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "#f3f4f6" }}>
            <p className="text-[11px] text-gray-500">Complete your profile to unlock more load invitations.</p>
            <button
              onClick={() => navigate("/settings")}
              className="text-[11px] font-bold underline underline-offset-2 shrink-0 ml-3 hover:opacity-80"
              style={{ color: BRASS }}
            >
              Update →
            </button>
          </div>
        </div>
      )}

      {openStat && (
        <StatDrillDownModal
          kind={openStat}
          jobs={completedJobs}
          driverProfile={driverProfile}
          onClose={() => setOpenStat(null)}
          onNavigateInvoices={() => { setOpenStat(null); navigate("/invoices"); }}
        />
      )}
    </div>
  );
}

function JobNotesBox({ jobId }) {
  const storageKey = `driver_job_note_${jobId}`;
  const [note, setNote] = useState(() => localStorage.getItem(storageKey) || "");
  const [saved, setSaved] = useState(true);

  useEffect(() => {
    setNote(localStorage.getItem(storageKey) || "");
    setSaved(true);
  }, [storageKey]);

  const handleChange = (val) => {
    setNote(val);
    setSaved(false);
  };

  const handleBlurSave = () => {
    localStorage.setItem(storageKey, note);
    setSaved(true);
  };

  return (
    <div className="rounded-2xl p-4 border" style={{ background: FILL_BRASS_SUBTLE, borderColor: BORDER_BRASS }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-black uppercase tracking-[0.15em] flex items-center gap-1.5" style={{ color: "#8A651F" }}>
          Notes for this job
        </p>
        <span className="text-[10px] text-gray-400">{saved ? "Saved" : "Unsaved…"}</span>
      </div>
      <textarea
        value={note}
        onChange={e => handleChange(e.target.value)}
        onBlur={handleBlurSave}
        placeholder="Meeting point directions, contact reminders, permit notes…"
        rows={2}
        className="w-full bg-white border rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-1 resize-none"
        style={{ borderColor: BORDER_BRASS }}
      />
    </div>
  );
}
